import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

interface PiiFieldRecord {
  table_name: string;
  column_name: string;
  is_pii: boolean;
}

/**
 * Hook that provides PII masking utilities for the current user.
 *
 * - Fetches PII field definitions for the user's client
 * - If the user has pii_enabled = false, provides functions to mask PII values
 * - If pii_enabled = true, masking functions pass through values unchanged
 * - Supports per-field unmasking with hourly rate limiting and audit logging
 */
export function usePiiMasking() {
  const { profile, user } = useAuth();
  const [piiFields, setPiiFields] = useState<PiiFieldRecord[]>([]);
  const [loading, setLoading] = useState(true);

  // Track which fields the user has unmasked in the current session
  const [unmaskedKeys, setUnmaskedKeys] = useState<Set<string>>(new Set());

  // Track hourly unmask count (fetched from audit_logs)
  const [hourlyUnmaskCount, setHourlyUnmaskCount] = useState(0);
  const hourlyCountRef = useRef(0);

  const shouldMask = profile ? !profile.pii_enabled : false;
  const hourlyLimit = profile?.hourly_unmask_limit ?? 0;

  useEffect(() => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }

    const fetchPiiFields = async () => {
      const { data } = await (supabase
        .from("pii_fields" as any)
        .select("table_name, column_name, is_pii")
        .eq("client_id", profile.client_id)
        .eq("is_pii", true) as any);
      setPiiFields((data as PiiFieldRecord[]) ?? []);
      setLoading(false);
    };

    fetchPiiFields();
  }, [profile?.client_id]);

  // Fetch hourly unmask count from audit_logs
  useEffect(() => {
    if (!shouldMask || !user?.id || !profile?.client_id) return;

    const fetchHourlyCount = async () => {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count } = await supabase
        .from("audit_logs")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("action", "accessed")
        .eq("resource_type", "pii_unmask" as any)
        .gte("created_at", oneHourAgo);
      const c = count ?? 0;
      setHourlyUnmaskCount(c);
      hourlyCountRef.current = c;
    };

    fetchHourlyCount();
    // Refresh count every 60 seconds
    const interval = setInterval(fetchHourlyCount, 60_000);
    return () => clearInterval(interval);
  }, [shouldMask, user?.id, profile?.client_id]);

  // Set of "table.column" keys that are PII
  const piiColumnSet = useMemo(() => {
    const set = new Set<string>();
    for (const f of piiFields) {
      set.add(`${f.table_name}.${f.column_name}`);
    }
    return set;
  }, [piiFields]);

  // Set of just column names that are PII (for cases where table is unknown)
  const piiColumnNames = useMemo(() => {
    const set = new Set<string>();
    for (const f of piiFields) {
      set.add(f.column_name.toLowerCase());
    }
    return set;
  }, [piiFields]);

  /**
   * Check if a given column (optionally with table) is a PII field.
   */
  const isPiiColumn = useCallback(
    (columnName: string, tableName?: string): boolean => {
      if (tableName) {
        return piiColumnSet.has(`${tableName}.${columnName}`);
      }
      return piiColumnNames.has(columnName.toLowerCase());
    },
    [piiColumnSet, piiColumnNames]
  );

  /**
   * Check if a field has been unmasked by the user.
   */
  const isUnmasked = useCallback(
    (key: string): boolean => unmaskedKeys.has(key),
    [unmaskedKeys]
  );

  /**
   * Attempt to unmask a field. Checks hourly limit, logs audit event.
   * Returns true if unmask succeeded, false if limit reached.
   */
  const requestUnmask = useCallback(
    async (key: string, fieldName?: string): Promise<boolean> => {
      if (!shouldMask || !user?.id || !profile?.client_id) return false;

      // Already unmasked
      if (unmaskedKeys.has(key)) return true;

      // Check hourly limit
      if (hourlyLimit <= 0) {
        toast.error("Hourly unmasking limit reached. Please contact Data Admin.");
        return false;
      }

      if (hourlyCountRef.current >= hourlyLimit) {
        toast.error("Hourly unmasking limit reached. Please contact Data Admin.");
        return false;
      }

      // Log audit event
      try {
        await supabase.from("audit_logs").insert({
          client_id: profile.client_id,
          user_id: user.id,
          user_email: user.email ?? null,
          user_name: profile.full_name ?? null,
          action: "accessed",
          resource_type: "pii_unmask" as any,
          resource_id: key,
          resource_name: fieldName ?? key,
          metadata: { field: key, timestamp: new Date().toISOString() },
        });
      } catch {
        // Audit logging is best-effort
      }

      // Update count
      hourlyCountRef.current += 1;
      setHourlyUnmaskCount((c) => c + 1);

      // Mark as unmasked
      setUnmaskedKeys((prev) => {
        const next = new Set(prev);
        next.add(key);
        return next;
      });

      return true;
    },
    [shouldMask, user?.id, user?.email, profile?.client_id, profile?.full_name, hourlyLimit, unmaskedKeys]
  );

  /**
   * Get remaining unmask attempts for this hour.
   */
  const remainingUnmasks = useMemo(() => {
    if (!shouldMask || hourlyLimit <= 0) return 0;
    return Math.max(0, hourlyLimit - hourlyUnmaskCount);
  }, [shouldMask, hourlyLimit, hourlyUnmaskCount]);

  /**
   * Mask a single value — replaces it with "***" if it's considered PII.
   */
  const maskValue = useCallback(
    (value: string | number | null | undefined, columnName: string, tableName?: string): string => {
      if (value == null) return "";
      if (!shouldMask) return String(value);
      if (isPiiColumn(columnName, tableName)) {
        return "●●●●●●";
      }
      return String(value);
    },
    [shouldMask, isPiiColumn]
  );

  /**
   * Mask all PII values within text content (chat messages, etc.).
   * Uses regex-based PII detection for inline text masking.
   */
  const maskText = useCallback(
    (text: string): string => {
      if (!shouldMask || !text) return text;

      let masked = text;

      // Email addresses
      masked = masked.replace(
        /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
        "●●●●●●"
      );

      // Phone numbers (various formats)
      masked = masked.replace(
        /(?:\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/g,
        "●●●●●●"
      );

      // Credit card numbers (basic patterns)
      masked = masked.replace(
        /\b(?:\d{4}[-\s]?){3}\d{4}\b/g,
        "●●●●●●"
      );

      // SSN
      masked = masked.replace(
        /\b\d{3}[-]?\d{2}[-]?\d{4}\b/g,
        "●●●●●●"
      );

      // Aadhaar (12 digits in groups of 4)
      masked = masked.replace(
        /\b\d{4}\s?\d{4}\s?\d{4}\b/g,
        "●●●●●●"
      );

      // IP addresses
      masked = masked.replace(
        /\b(?:\d{1,3}\.){3}\d{1,3}\b/g,
        "●●●●●●"
      );

      return masked;
    },
    [shouldMask]
  );

  /**
   * Mask row data from table/chart widgets.
   * Takes an array of rows (objects) and masks values whose keys match PII columns.
   */
  const maskRows = useCallback(
    (rows: Record<string, any>[], tableName?: string): Record<string, any>[] => {
      if (!shouldMask) return rows;
      return rows.map((row) => {
        const masked: Record<string, any> = {};
        for (const [key, value] of Object.entries(row)) {
          if (isPiiColumn(key, tableName)) {
            masked[key] = "●●●●●●";
          } else {
            masked[key] = value;
          }
        }
        return masked;
      });
    },
    [shouldMask, isPiiColumn]
  );

  /**
   * Mask table widget data (columns and string[][] rows).
   */
  const maskTableRows = useCallback(
    (columns: string[], rows: string[][]): string[][] => {
      if (!shouldMask) return rows;
      return rows.map((row) =>
        row.map((cell, i) => {
          const colName = columns[i];
          if (colName && isPiiColumn(colName)) {
            return "●●●●●●";
          }
          return cell;
        })
      );
    },
    [shouldMask, isPiiColumn]
  );

  return {
    shouldMask,
    loading,
    piiFields,
    isPiiColumn,
    maskValue,
    maskText,
    maskRows,
    maskTableRows,
    // Unmasking utilities
    isUnmasked,
    requestUnmask,
    remainingUnmasks,
    hourlyLimit,
  };
}
